/**
 * vertexClient.js
 *
 * Handles communication with the LLM backend (Gemini via the generateProxy
 * Cloud Function). Implements the 3-tier cost model and falls back to
 * deterministic stubs only if the backend is unreachable or auth is missing.
 *
 * The returned object carries the NPC response schema plus three non-schema
 * diagnostic fields the evals layer reads: __source ("live" | "mock"),
 * __model, and __latencyMs. conversationManager.js ignores unknown keys, so
 * these are inert to gameplay.
 */
import { functions, authReady, getFunctionUrl } from './firebase.js';
import { httpsCallable } from 'firebase/functions';
import { getAccessCode } from './accessCode.js';
import { createDialogueExtractor } from './streamExtract.js';

// All tiers route to Flash. Context management (chunked retrieval, rolling
// summarization, prompt caching) keeps token counts low enough that Flash
// delivers quality responses at a fraction of Pro's cost.
const MODEL_BY_TIER = {
  1: 'gemini-2.5-flash',
  2: 'gemini-2.5-flash',
  3: 'gemini-2.5-flash',
};

export function modelForTier(tier) {
  return MODEL_BY_TIER[tier] || MODEL_BY_TIER[3];
}

// Per-turn correlation id. Sent with the proxy call so the server's turn
// telemetry doc and the client's eval upload (recordEval) key the same record.
function newTurnId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (err) { /* ignore */ }
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export async function checkDailyBudget() {
  if (!functions) return { available: true };
  const TIMEOUT_MS = 4000;
  const timeout = new Promise((resolve) =>
    setTimeout(() => resolve({ available: true, timedOut: true }), TIMEOUT_MS)
  );
  const check = (async () => {
    try {
      await authReady;
      const fn = httpsCallable(functions, 'checkBudget');
      const response = await fn({ accessCode: getAccessCode() });
      return response.data;
    } catch (err) {
      console.error('Budget check failed:', err);
      return { available: true };
    }
  })();
  return Promise.race([check, timeout]);
}

/**
 * Send a STRUCTURED reference request (Shape A) to generateProxy. The server
 * assembles Layer 1 + Layer 3 (+ phase-gated case facts) into systemInstruction
 * and the Layer 2 dynamic block + labeled input into the prompt. The client
 * holds no Layer 1/Layer 3/case-knowledge text on this path.
 *
 * On a backend fault or missing auth, falls back to the same deterministic mock
 * as the legacy path, using the request's userInput as the mock seed.
 *
 * When an onToken callback is supplied, the turn first attempts the streaming
 * endpoint (generateStream); onToken receives decoded increments of the
 * response's "dialogue" field as they arrive. On ANY stream or network fault
 * the turn falls back to the callable, so behavior without a working stream
 * is identical to today.
 *
 * @param {string} npcId
 * @param {Object} structuredRequest - from buildStructuredRequest()
 * @param {number} tier
 * @param {boolean} [isAmbient]
 * @param {(text: string) => void} [onToken]
 */
export async function fetchStructuredResponse(npcId, structuredRequest, tier, isAmbient = false, onToken = null) {
  const effectiveTier = isAmbient ? Math.max(tier, 2) : tier;
  const modelName = modelForTier(effectiveTier);
  const mockSeed = structuredRequest?.userInput || '';

  if (!functions) {
    console.warn(`[Vertex Client Mock] Routing structured request to Tier ${effectiveTier} for ${npcId}`);
    return getMockResponse(npcId, mockSeed, isAmbient, modelName);
  }

  const startedAt = Date.now();
  // Shape A: structured request. The server rejects raw prompt/systemInstruction
  // on this path, so we send only references + dynamic state + userInput.
  const payload = { ...structuredRequest, model: modelName, accessCode: getAccessCode(), turnId: newTurnId() };

  if (typeof onToken === 'function' && typeof fetch === 'function' && typeof ReadableStream !== 'undefined') {
    try {
      return await fetchStructuredStream(payload, onToken, modelName, startedAt);
    } catch (err) {
      console.warn('[Vertex Client] Stream path failed, falling back to callable:', err?.message || err);
    }
  }

  try {
    await authReady;
    const generateProxy = httpsCallable(functions, 'generateProxy');
    const response = await generateProxy(payload);
    return parseProxyResult(response.data, modelName, startedAt);
  } catch (err) {
    return handleProxyError(err, npcId, mockSeed, isAmbient, modelName, startedAt);
  }
}

/**
 * Streaming transport: POST the structured payload to the generateStream
 * onRequest endpoint and consume its newline-delimited JSON events with a
 * ReadableStream reader. "tok" events carry raw model text (the in-progress
 * JSON body); they feed the tolerant partial-JSON extractor which surfaces
 * ONLY the decoded "dialogue" string contents through onToken. The "done"
 * event carries the same envelope the callable returns, parsed by the same
 * parseProxyResult so evals, trust, and telemetry see an identical shape.
 * Throws on any fault; the caller falls back to the callable.
 */
async function fetchStructuredStream(payload, onToken, modelName, startedAt) {
  const user = await authReady;
  if (!user || typeof user.getIdToken !== 'function') {
    throw new Error('No authenticated user for streaming');
  }
  const idToken = await user.getIdToken();

  const res = await fetch(getFunctionUrl('generateStream'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Stream endpoint HTTP ${res.status}`);
  if (!res.body || typeof res.body.getReader !== 'function') {
    throw new Error('Streaming body unavailable');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const extractor = createDialogueExtractor(onToken);
  let buffer = '';
  let donePayload = null;

  const handleLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const msg = JSON.parse(trimmed);
    if (msg.t === 'tok' && typeof msg.text === 'string') {
      extractor.push(msg.text);
    } else if (msg.t === 'done') {
      donePayload = msg.payload;
    } else if (msg.t === 'err') {
      throw new Error(msg.message || 'Stream reported an error');
    }
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl;
      while ((nl = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 1);
        handleLine(line);
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) handleLine(buffer);
  } catch (err) {
    try { reader.cancel(); } catch (cancelErr) { /* connection already gone */ }
    throw err;
  }
  extractor.end();

  if (!donePayload) throw new Error('Stream ended without completion envelope');
  // Parse the complete JSON exactly as the non-streaming path does.
  return parseProxyResult(donePayload, modelName, startedAt);
}

export async function fetchNpcResponse(npcId, prompt, tier, isAmbient, systemInstruction) {
  // Enforce the Ambient state cost reduction (always drops to Flash tier).
  const effectiveTier = isAmbient ? Math.max(tier, 2) : tier;
  const modelName = modelForTier(effectiveTier);

  if (!functions) {
    console.warn(`[Vertex Client Mock] Routing to Tier ${effectiveTier} for ${npcId}`);
    return getMockResponse(npcId, prompt, isAmbient, modelName);
  }

  const startedAt = Date.now();
  try {
    // The callable function rejects unauthenticated requests; wait for the
    // anonymous session before the first call.
    await authReady;

    const generateProxy = httpsCallable(functions, 'generateProxy');
    const payload = { model: modelName, prompt, accessCode: getAccessCode(), turnId: newTurnId() };
    if (systemInstruction) payload.systemInstruction = systemInstruction;
    const response = await generateProxy(payload);
    return parseProxyResult(response.data, modelName, startedAt);
  } catch (err) {
    return handleProxyError(err, npcId, prompt, isAmbient, modelName, startedAt);
  }
}

/**
 * Parse the generateProxy result envelope ({ response, tier, model,
 * usageMetadata }) into the NPC response schema plus the diagnostic fields the
 * evals layer reads. Shared by the structured (Shape A) and legacy (Shape C)
 * paths so both behave identically.
 */
function parseProxyResult(result, modelName, startedAt) {
  const text = result.response?.candidates?.[0]?.content?.parts?.[0]?.text;
  if (!text) {
    throw new Error('Invalid response structure from Gemini proxy');
  }
  const parsed = JSON.parse(text);
  parsed.__source = 'live';
  parsed.__model = result.model || modelName;
  parsed.__tier = result.tier || 'public';
  parsed.__turnId = result.turnId || null;
  parsed.__latencyMs = Date.now() - startedAt;
  const usage = result.usageMetadata;
  parsed.__tokenUsage = usage
    ? {
        promptTokens: usage.promptTokenCount ?? 0,
        candidateTokens: usage.candidatesTokenCount ?? 0,
        totalTokens: usage.totalTokenCount ?? 0,
        cachedTokens: usage.cachedContentTokenCount ?? 0,
      }
    : null;
  return parsed;
}

/**
 * Handle a generateProxy failure: surface session/daily cap exhaustion as a
 * graceful demo-end signal, otherwise degrade to the deterministic mock so the
 * demo never hard-stops on a backend fault. Shared by both call paths.
 */
async function handleProxyError(err, npcId, mockSeed, isAmbient, modelName, startedAt) {
  console.error('LLM Request Failed:', err);
  const errMsg = err?.message || String(err);
  if (err?.code === 'resource-exhausted' || errMsg.includes('resource-exhausted') || errMsg.includes('session limit') || errMsg.includes('daily limit')) {
    return {
      dialogue: '',
      __source: 'exhausted',
      __sessionExhausted: true,
      __error: errMsg,
      __latencyMs: Date.now() - startedAt,
    };
  }
  const mock = await getMockResponse(npcId, mockSeed, isAmbient, modelName);
  mock.__error = errMsg;
  mock.__latencyMs = Date.now() - startedAt;
  return mock;
}

/**
 * Generates a condensed summary of conversation turns to maintain context 
 * without exceeding token limits. Uses the fast Tier 2 (Flash) model.
 */
export async function fetchSummary(turnsText, currentSummary = "") {
  const modelName = MODEL_BY_TIER[2]; // gemini-2.5-flash
  const prompt = `
You are a context-summarization subsystem for an interactive detective game.
Your task is to take the current running summary and the latest conversation turns, and output a new, condensed summary of the entire interaction so far.
Focus ONLY on factual discoveries, shifts in character trust/emotion, and key evidence discussed. Keep it extremely brief ("caveman summary").

CURRENT SUMMARY:
${currentSummary || "None"}

NEW TURNS TO INTEGRATE:
${turnsText}

Return ONLY the plain text of the new summary. No markdown formatting, no JSON, just the summary text.
  `.trim();

  if (!functions) {
    return currentSummary + (currentSummary ? " " : "") + "[Mock Summary Added]";
  }

  try {
    await authReady;
    const generateProxy = httpsCallable(functions, 'generateProxy');
    const response = await generateProxy({ model: modelName, prompt, accessCode: getAccessCode() });
    
    let text = response.data?.response?.candidates?.[0]?.content?.parts?.[0]?.text || "";
    // Clean up potential markdown formatting from the model if it disobeys
    text = text.replace(/^```[a-z]*\n/i, '').replace(/\n```$/i, '').trim();
    return text || currentSummary;
  } catch (err) {
    console.error('Summarization Request Failed:', err);
    return currentSummary; // If it fails, just keep the old summary to avoid catastrophic forgetting
  }
}

/**
 * Deterministic stubs for when the LLM is unreachable or disabled.
 */
function getMockResponse(npcId, prompt, isAmbient, modelName) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        dialogue: isAmbient
          ? `[Ambient fallback] Yeah, let me tell you a story about ${npcId}.`
          : `[Offline fallback] I heard what you said. The live model is unreachable, so this line is deterministic.`,
        trust_delta: 0.05,
        trust_reason: 'Player engaged in polite conversation.',
        revealed: [],
        emotional_state: 'neutral',
        flags: [],
        __source: 'mock',
        __model: modelName,
        __tokenUsage: null,
      });
    }, 800);
  });
}
